import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { money, monthLabel, addMonth, DEMO_MONTH, fmtDayLong } from "../lib/format";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from "lucide-react";
import { TipBanner } from "../components/TipBanner";

export default function Work() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(DEMO_MONTH);
  const { data, isLoading } = useQuery({
    queryKey: ["work", month],
    queryFn: async () => (await api.get(`/work?month=${month}`)).data,
  });

  const total = (data || []).reduce((s, e) => s + (e.expected_cents || 0), 0);

  return (
    <AppShell>
      <PageHeader title="Work" subtitle={`${money(total)} expected this month`} />

      <TipBanner id="work" text="Tip: Add a work day and Molaroo automatically calculates your expected earnings from that practice's agreement." />

      <div className="px-5">
        <div className="df-card flex items-center justify-between px-2 py-2 mb-4">
          <button data-testid="work-month-prev" onClick={() => setMonth(addMonth(month, -1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <span className="font-heading font-semibold text-slate-900">{monthLabel(month)}</span>
          <button data-testid="work-month-next" onClick={() => setMonth(addMonth(month, 1))}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center df-pressable">
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      <div className="px-5 space-y-3 df-fade-in">
        {isLoading ? (
          <div className="text-slate-400 text-sm px-1">Loading…</div>
        ) : data?.length ? (
          data.map((e) => (
            <button key={e.id} data-testid={`work-entry-${e.id}`}
              onClick={() => navigate(`/work/${e.id}/edit`, { state: { entry: e } })}
              className="w-full text-left df-card p-4 df-pressable">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-xs font-semibold tracking-wide uppercase text-[#0D786A]">{fmtDayLong(e.date)}</div>
                  <div className="font-heading font-semibold text-slate-900 truncate">{e.practice_name}</div>
                  {e.start_time && (
                    <div className="text-xs text-slate-500 mt-0.5">{e.start_time}–{e.finish_time}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {e._calc_pending || e.expected_cents == null ? (
                    <div className="font-num font-semibold text-sm text-amber-600" data-testid={`work-pending-${e.id}`}>Pending</div>
                  ) : (
                    <div className="font-num font-bold text-lg text-slate-900">{money(e.expected_cents)}</div>
                  )}
                  <div className="mt-1"><StatusBadge status={e.status} testId={`work-status-${e.id}`} /></div>
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="df-card p-8 text-center">
            <span className="mx-auto h-12 w-12 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center mb-3">
              <CalendarDays size={22} />
            </span>
            <div className="font-heading font-semibold text-slate-900 text-lg">No work recorded yet</div>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Add your first work day and Molaroo will calculate your expected earnings using your practice agreement.
            </p>
            <Button data-testid="empty-add-work" onClick={() => navigate("/work/add")}
              className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">Add Work</Button>
          </div>
        )}
      </div>

      <div className="h-[calc(12.5rem+env(safe-area-inset-bottom))] lg:h-24" aria-hidden />

      <button data-testid="add-work-day-button" onClick={() => navigate("/work/add")}
        className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] lg:bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 h-12 rounded-full
                   bg-[#0D786A] text-white font-semibold shadow-lg shadow-emerald-900/20 df-pressable">
        <Plus size={20} /> Add Work
      </button>
    </AppShell>
  );
}
